import React, { useCallback, useEffect, useState } from 'react';
import { IndianRupee, TrendingUp, Award, Sparkles, Target, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Employee Earnings tab — shows base incentive, campaign bonus, quality
 * multiplier and total payout for the selected period.
 *
 * Endpoint:  GET /api/panel/employee/earnings?period=month|week|today
 */
export default function EmployeeEarningsTab({ api }) {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('GET', `/employee/earnings?period=${period}`);
      setData(res.data || null);
    } catch (e) {
      console.warn('earnings load failed', e?.message);
      setData(null);
    } finally { setLoading(false); }
  }, [api, period]);

  useEffect(() => { load(); }, [load]);

  const fmt = (n) => `₹${Number(n || 0).toLocaleString()}`;
  const periods = [
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' },
  ];

  return (
    <div className="space-y-5">
      {/* Period switcher */}
      <div className="inline-flex p-1 bg-slate-100 rounded-lg">
        {periods.map((p) => (
          <Button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            variant="ghost"
            data-testid={`emp-earnings-period-${p.id}`}
            className={`h-8 px-4 text-xs font-semibold rounded-md ${period === p.id ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:bg-white/60'}`}
          >
            {p.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>
      ) : data ? (
        <>
          {/* Hero earnings card */}
          <div
            className="rounded-2xl p-6 text-white shadow-xl"
            style={{ background: 'linear-gradient(135deg, #047857 0%, #0F766E 100%)' }}
            data-testid="emp-earnings-hero"
          >
            <div className="text-xs uppercase tracking-widest text-emerald-100 font-semibold flex items-center gap-1.5 mb-2">
              <Sparkles size={13} /> Estimated payout · {periods.find((p) => p.id === period)?.label}
            </div>
            <div className="text-4xl sm:text-5xl font-extrabold tracking-tight flex items-baseline gap-1">
              <IndianRupee size={28} className="opacity-80 -mr-1" />
              {Number(data.total).toLocaleString()}
            </div>
            <div className="mt-2 text-sm text-emerald-50/90">
              {data.jobs_completed} job{data.jobs_completed === 1 ? '' : 's'} completed
              {data.matched_slab && <> · Slab: <span className="font-semibold">{data.matched_slab}</span></>}
            </div>
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <BreakdownTile
              icon={Award}
              label="Base Incentive"
              value={fmt(data.base_incentive)}
              hint={data.matched_slab || 'Complete more jobs to reach next slab'}
              accent="text-emerald-700 bg-emerald-50 border-emerald-200"
            />
            <BreakdownTile
              icon={TrendingUp}
              label="Campaign Bonus"
              value={fmt(data.campaign_bonus)}
              hint={data.campaign_bonus > 0 ? 'From active bonus campaigns' : 'No active campaign matches'}
              accent="text-amber-700 bg-amber-50 border-amber-200"
            />
            <BreakdownTile
              icon={Target}
              label="Quality Multiplier"
              value={`× ${data.quality_multiplier}`}
              hint={data.quality_multiplier > 1 ? 'Higher customer ratings = bigger payout' : data.quality_multiplier < 1 ? 'Improve ratings to boost' : 'Neutral — earn your first ratings'}
              accent="text-sky-700 bg-sky-50 border-sky-200"
            />
          </div>

          <div className="text-xs text-slate-500 leading-relaxed">
            Earnings update in real time as you complete jobs. Final payout is processed at month-end by Admin and may include hold/release adjustments.
          </div>
        </>
      ) : (
        <div className="text-sm text-slate-500">No earnings data available yet.</div>
      )}
    </div>
  );
}

function BreakdownTile({ icon: Icon, label, value, hint, accent }) {
  return (
    <div className={`rounded-xl border p-4 ${accent}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold opacity-80">
        <Icon size={12} /> {label}
      </div>
      <div className="text-2xl font-bold mt-1.5">{value}</div>
      <div className="text-[11px] mt-1 opacity-70">{hint}</div>
    </div>
  );
}
